import React, { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import ErrorHandler from '../../utils/errorHandler';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';

const AcceptInvitation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { isAuthenticated } = useAuth();

  const token = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('token') || '';
  }, [location.search]);

  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [accepted, setAccepted] = useState(false);

  const handleAccept = async (e) => {
    e.preventDefault();
    if (!token) {
      showToast('Missing invitation token', 'error');
      return;
    }

    try {
      setLoading(true);
      await api.post('/invitations/accept', {
        token,
        name: name || undefined,
        password: password || undefined,
      });
      setAccepted(true);
      showToast('Invitation accepted', 'success');
    } catch (error) {
      const message = ErrorHandler.getErrorMessage(error);
      showToast(message, 'error');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0f1115] flex items-center justify-center p-6">
        <Card>
          <div className="text-center py-8">
            <h1 className="text-xl font-bold text-white">Invalid invitation link</h1>
            <p className="mt-2 text-sm text-[#d8d2c5]">The invitation token is missing.</p>
            <Link to="/">
              <Button className="mt-6">Go Home</Button>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  if (accepted) {
    return (
      <div className="min-h-screen bg-[#0f1115] flex items-center justify-center p-6">
        <Card>
          <div className="text-center py-8">
            <h1 className="text-xl font-bold text-white">You’re in</h1>
            <p className="mt-2 text-sm text-[#d8d2c5]">The invitation was accepted successfully.</p>
            <div className="mt-6 flex gap-3 justify-center">
              {isAuthenticated ? (
                <Button onClick={() => navigate('/chamas')}>Go to My Chamas</Button>
              ) : (
                <Link to="/login">
                  <Button>Log in</Button>
                </Link>
              )}
              <Link to="/">
                <Button variant="outline">Home</Button>
              </Link>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0f1115] flex items-center justify-center p-6">
      <Card>
        <div className="py-2">
          <h1 className="text-2xl font-bold text-white">Accept invitation</h1>
          <p className="mt-1 text-sm text-[#d8d2c5]">
            If you don’t have an account yet, set your name and password.
          </p>

          <form onSubmit={handleAccept} className="mt-6 space-y-4">
            <Input
              label="Full name (new users)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
            />
            <Input
              label="Password (new users)"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Create a password"
            />

            <Button type="submit" fullWidth loading={loading}>
              Accept Invitation
            </Button>
          </form>

          <p className="mt-4 text-xs text-[#d8d2c5]">
            Already have an account? You can accept and then log in.
          </p>
        </div>
      </Card>
    </div>
  );
};

export default AcceptInvitation;
